import { useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import { bootstrapSchema, type BootstrapResponse } from "../api/schemas";
import { ApiSessionChangedError } from "../api/api-client";
import type { WalletSessionController } from "../wallet/wallet-session";
import { errorMessage, sameAddress } from "./model-helpers";
import { validateConnectedDeployment } from "../config/elysium";

type AppBootstrapOptions = {
  walletController: WalletSessionController;
  activeAddress: string | null;
  setActiveAddress: Dispatch<SetStateAction<string | null>>;
  tokenRevision: RefObject<number>;
  refreshRevision: number;
};

export function useAppBootstrap({ walletController, activeAddress, setActiveAddress,
  tokenRevision, refreshRevision }: AppBootstrapOptions) {
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const bootstrapGeneration = useRef(0);
  const refreshBootstrap = useRef<() => void>(() => {});
  const committedRefreshRevision = useRef(refreshRevision);
  const lastBootstrapRefreshRevision = useRef(refreshRevision);

  useEffect(() => { committedRefreshRevision.current = refreshRevision; }, [refreshRevision]);
  useEffect(() => {
    const abort = new AbortController();
    const generation = ++bootstrapGeneration.current;
    // The initial read also serves a refresh committed with this token change.
    lastBootstrapRefreshRevision.current = committedRefreshRevision.current;
    let pending = false;
    let queued = false;
    const load = async () => {
      if (pending || abort.signal.aborted) return;
      pending = true;
      const selection = tokenRevision.current;
      try {
        const result = await walletController.api.get("/app/bootstrap", bootstrapSchema, abort.signal);
        if (abort.signal.aborted || generation !== bootstrapGeneration.current || selection !== tokenRevision.current) return;
        validateConnectedDeployment(result);
        walletController.setExpectedSiweContext(result.siwe);
        setBootstrap(result);
        setBootstrapError(null);
        if (!activeAddress || !result.tokens.some((token) => sameAddress(token.tokenAddress, activeAddress))) {
          setActiveAddress(result.tokens[0]?.tokenAddress ?? null);
        }
      } catch (error) {
        if (!abort.signal.aborted && generation === bootstrapGeneration.current && selection === tokenRevision.current) {
          if (error instanceof ApiSessionChangedError) queued = true;
          else {
            walletController.clearExpectedSiweContext();
            setBootstrap(null);
            setBootstrapError(errorMessage(error));
          }
        }
      } finally {
        pending = false;
        if (queued && !abort.signal.aborted) { queued = false; poll(); }
      }
    };
    const poll = () => { load().catch(() => console.error("Bootstrap polling failed unexpectedly.")); };
    const refresh = () => { if (pending) queued = true; else poll(); };
    refreshBootstrap.current = refresh;
    poll();
    const backgroundPoll = () => { if (document.visibilityState !== "hidden") poll(); };
    const timer = setInterval(backgroundPoll, 30_000);
    document.addEventListener("visibilitychange", backgroundPoll);
    return () => {
      abort.abort(); clearInterval(timer);
      document.removeEventListener("visibilitychange", backgroundPoll);
      if (refreshBootstrap.current === refresh) refreshBootstrap.current = () => {};
    };
  }, [walletController, activeAddress, setActiveAddress, tokenRevision]);
  useEffect(() => {
    if (lastBootstrapRefreshRevision.current !== refreshRevision) {
      lastBootstrapRefreshRevision.current = refreshRevision;
      refreshBootstrap.current();
    }
  }, [refreshRevision]);

  return { bootstrap, bootstrapError };
}
